import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Paper,
    Stack,
    Collapse,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { listPhysicalExamSystems } from '../../../../api/emrMaster';
import SectionHeading from './SectionHeading';

const NAVY = '#052F5D';

/**
 * Renders the 13-system exam form. The master systems list defines the
 * rows the clinician sees; the encounter only stores rows that have a
 * non-default status. That means tapping into this tab does NOT dirty
 * the encounter (was a bug — previously hydrated all 13 rows on mount
 * and triggered an autosave for an unedited tab).
 */
export default function PhysicalExamTab({ encounter, onPatch, disabled }) {
    const [systems, setSystems] = useState([]);
    useEffect(() => {
        let cancelled = false;
        listPhysicalExamSystems()
            .then((r) => { if (!cancelled) setSystems(r.data || []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const stored = encounter?.physicalExam || [];
    const byName = useMemo(() => {
        const m = new Map();
        stored.forEach((r) => m.set(r.system, r));
        return m;
    }, [stored]);

    const rows = systems.length ? systems : stored.map((r) => ({ system: r.system, checkFor: '' }));

    const setRow = (systemName, patch) => {
        const existing = byName.get(systemName);
        const merged = existing
            ? stored.map((r) => (r.system === systemName ? { ...r, ...patch } : r))
            : [...stored, { system: systemName, status: 'not_examined', findings: '', ...patch }];
        onPatch({ physicalExam: merged });
    };

    return (
        <Box>
            <SectionHeading
                title="Physical examination"
                hint="Mark each system as Normal or Abnormal. Add a note when something looks off."
            />
            <Stack spacing={1.25}>
                {rows.map((meta) => {
                    const row = byName.get(meta.system) || { status: 'not_examined', findings: '' };
                    const isAbnormal = row.status === 'abnormal';
                    return (
                        <Paper
                            key={meta.system}
                            variant="outlined"
                            sx={{
                                px: { xs: 1.5, md: 2 },
                                py: 1.25,
                                borderRadius: 1.5,
                                borderColor: isAbnormal ? 'rgba(196,106,0,0.35)' : 'rgba(5,47,93,0.10)',
                                bgcolor: isAbnormal ? '#FFFBF1' : '#fff',
                                transition: 'background-color 160ms ease, border-color 160ms ease',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { sm: 'center' },
                                    gap: { xs: 1, sm: 2 },
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>
                                        {meta.system}
                                    </Typography>
                                    {meta.checkFor && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
                                        >
                                            {meta.checkFor}
                                        </Typography>
                                    )}
                                </Box>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={row.status === 'not_examined' ? null : row.status}
                                    onChange={(_, v) => v && !disabled && setRow(meta.system, { status: v })}
                                    sx={{
                                        '& .MuiToggleButton-root': {
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            px: 1.5,
                                            borderColor: 'rgba(5,47,93,0.15)',
                                            color: 'text.secondary',
                                            '&.Mui-selected': { fontWeight: 600 },
                                        },
                                    }}
                                >
                                    <ToggleButton
                                        value="normal"
                                        disabled={disabled}
                                        sx={{
                                            '&.Mui-selected': {
                                                color: 'success.dark',
                                                bgcolor: 'rgba(46,125,50,0.08)',
                                                borderColor: 'rgba(46,125,50,0.3)',
                                                '&:hover': { bgcolor: 'rgba(46,125,50,0.12)' },
                                            },
                                        }}
                                    >
                                        <CheckRoundedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                        Normal
                                    </ToggleButton>
                                    <ToggleButton
                                        value="abnormal"
                                        disabled={disabled}
                                        sx={{
                                            '&.Mui-selected': {
                                                color: '#A65300',
                                                bgcolor: '#FFF1D6',
                                                borderColor: '#E6A23C',
                                                '&:hover': { bgcolor: '#FFE9BF' },
                                            },
                                        }}
                                    >
                                        <WarningAmberRoundedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                        Abnormal
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            <Collapse in={isAbnormal} unmountOnExit>
                                <Box sx={{ pt: 1.25 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        multiline
                                        minRows={1}
                                        placeholder="Describe abnormal findings…"
                                        value={row.findings || ''}
                                        disabled={disabled}
                                        onChange={(e) => setRow(meta.system, { findings: e.target.value })}
                                        InputProps={{
                                            sx: {
                                                bgcolor: '#fff',
                                                '& fieldset': { borderColor: 'rgba(196,106,0,0.25)' },
                                            },
                                        }}
                                    />
                                </Box>
                            </Collapse>
                        </Paper>
                    );
                })}
            </Stack>
        </Box>
    );
}
